/**
 * Autuação: a evasão que espera o envio ao autuador (`T-21`, ADR-085).
 *
 * A primeira área que só existe pelo contrato: o menu a mostra — e a rota responde — onde o
 * contrato de alguma concessão do recorte manda a concessionária enviar a evasão ao autuador (em
 * MS, o relatório semanal à AGESUL, que não leva registro com mais de 7 dias da evasão). Na
 * concessão que só valida, ela não aparece, e o endereço digitado volta 404.
 *
 * Só leitura. Por concessão, quatro números — no prazo, perto do fim, no último dia, fora do
 * prazo —, que filtram a fila, e a fila das mais urgentes. A situação de cada evasão vem do
 * servidor, pela regra do domínio (`transit_domain.autuacao`): a tela não refaz a conta. O
 * envio é a `OB-17`, e a tela diz isso no topo.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  AutuacaoDaConcessao,
  AutuacaoNaTela,
  EvasaoNaFila,
} from "../api/tipos";
import { Badge, Estado, Kpi, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import type { Significado } from "../componentes/tinta";
import { dataCurta, reais, restante } from "../formato";
import { useDeslocamento } from "../relogio";
import { repetirComAbaVisivel } from "../ganchos/repetirComAbaVisivel";

/** De quanto em quanto a tela relê: o motor de prazos roda por minuto. */
const RELEITURA_MS = 60_000;

type Situacao = EvasaoNaFila["situacao"];

const SITUACOES: {
  situacao: Situacao;
  rotulo: string;
  campo: "no_prazo" | "perto_do_fim" | "ultimo_dia" | "fora_do_prazo";
  severidade: "neutro" | "atencao" | "critico";
  cor: Significado;
}[] = [
  {
    situacao: "NO_PRAZO",
    rotulo: "No prazo",
    campo: "no_prazo",
    severidade: "neutro",
    cor: "verde",
  },
  {
    situacao: "PERTO_DO_FIM",
    rotulo: "Perto do fim",
    campo: "perto_do_fim",
    severidade: "atencao",
    cor: "amarelo",
  },
  {
    situacao: "ULTIMO_DIA",
    rotulo: "Último dia",
    campo: "ultimo_dia",
    severidade: "critico",
    cor: "vermelho",
  },
  {
    situacao: "FORA_DO_PRAZO",
    rotulo: "Fora do prazo",
    campo: "fora_do_prazo",
    severidade: "critico",
    cor: "cinza",
  },
];

const COR: Record<Situacao, Significado> = Object.fromEntries(
  SITUACOES.map((s) => [s.situacao, s.cor]),
) as Record<Situacao, Significado>;

export function Autuacao() {
  const [tela, definirTela] = useState<AutuacaoNaTela | null>(null);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);
  // O relógio da organização mudou (o avanço da demonstração): a conta é outra (ADR-059).
  const deslocamento = useDeslocamento();

  const ultima = useRef(0);
  const carregar = useCallback(async () => {
    const esta = ++ultima.current;
    try {
      const lida = await api<AutuacaoNaTela>("/tollvision/autuacao");
      if (esta !== ultima.current) return;
      definirTela(lida);
      definirErro(null);
    } catch (falha) {
      if (esta !== ultima.current) return;
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
    }
  }, []);

  useEffect(() => {
    void deslocamento;
    void carregar();
    const parar = repetirComAbaVisivel(() => void carregar(), RELEITURA_MS);
    return () => {
      ultima.current++;
      parar();
    };
  }, [carregar, deslocamento]);

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Autuação
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            A evasão que espera o envio ao autuador, e até quando ela pode ir
          </p>
        </div>
      </div>

      {erro ? (
        <Estado
          tipo={
            erro.status === 402
              ? "sem-licenca"
              : erro.status === 403
                ? "bloqueado"
                : erro.status === 404
                  ? "vazio"
                  : "erro"
          }
          titulo={
            erro.status === 404
              ? "O contrato de nenhuma concessão do seu recorte manda a evasão ao autuador."
              : erro.status === 403
                ? "A fila do autuador é da supervisão e do backoffice."
                : "Não foi possível ler a fila do autuador."
          }
          detalhe={erro.message}
        />
      ) : tela === null ? (
        <Estado tipo="carregando" titulo="Carregando…" />
      ) : (
        <div className="grid min-h-0 content-start gap-3">
          <p
            role="note"
            className="flex flex-wrap items-center gap-2 text-[11.5px] text-tinta-3"
          >
            <Badge cor="amarelo">ainda não sai</Badge>
            {tela.aviso}
          </p>
          {tela.concessoes.map((c) => (
            <DaConcessao
              key={c.concessao_id}
              concessao={c}
              agora={new Date(tela.agora)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function DaConcessao({
  concessao,
  agora,
}: {
  concessao: AutuacaoDaConcessao;
  agora: Date;
}) {
  const [filtro, definirFiltro] = useState<Situacao | null>(null);
  const fila = filtro
    ? concessao.fila.filter((e) => e.situacao === filtro)
    : concessao.fila;
  return (
    <section
      aria-label={`${concessao.concessao} · ${concessao.codigo}`}
      className="grid min-h-0 gap-3"
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {SITUACOES.map((s) => (
          <Kpi
            key={s.situacao}
            rotulo={s.rotulo}
            valor={concessao[s.campo]}
            icone={<Icone.Relogio />}
            severidade={s.severidade}
            ativo={filtro === s.situacao}
            aoClicar={() =>
              definirFiltro((atual) =>
                atual === s.situacao ? null : s.situacao,
              )
            }
            compacto
          />
        ))}
      </div>
      <Painel
        titulo={`${concessao.concessao} · ${concessao.codigo} — ${concessao.autuador}`}
        acoes={
          <span className="text-[11px] text-tinta-3">
            {concessao.abertas} a enviar ·{" "}
            {reais(concessao.valor_no_prazo_centavos)} ainda no prazo
            {concessao.sem_tarifa > 0 &&
              ` (${concessao.sem_tarifa} sem tarifa)`}
          </span>
        }
        rodape={
          <span>
            Prazo do autuador: {concessao.prazo}. Fonte: {concessao.fonte}.
            {concessao.mais &&
              ` A fila mostra as ${concessao.fila.length} mais urgentes.`}
          </span>
        }
        rolagemPropria
      >
        {fila.length === 0 ? (
          <Estado
            tipo="vazio"
            titulo={
              filtro
                ? "Nenhuma evasão nesta situação."
                : "Nenhuma evasão esperando o envio ao autuador."
            }
          />
        ) : (
          <div className="min-h-0 overflow-auto">
            <table className="w-full min-w-[720px] border-collapse text-[11.5px]">
              <thead className="sticky top-0 bg-painel-2 text-left text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
                <tr>
                  <th className="px-2 py-1.5">Placa</th>
                  <th className="px-2 py-1.5">Praça</th>
                  <th className="px-2 py-1.5">Passagem</th>
                  <th className="px-2 py-1.5">Evasão</th>
                  <th className="px-2 py-1.5">Enviar até</th>
                  <th className="px-2 py-1.5 text-right">Valor</th>
                  <th className="px-2 py-1.5">Situação</th>
                </tr>
              </thead>
              <tbody>
                {fila.map((e) => (
                  <tr
                    key={e.transito_id}
                    className="border-t border-linha-suave"
                  >
                    <td className="mono px-2 py-1.5 font-bold text-tinta">
                      {e.placa ?? "sem placa"}
                    </td>
                    <td className="px-2 py-1.5 text-tinta-2">{e.praca}</td>
                    <td className="mono px-2 py-1.5 text-tinta-2">
                      {dataCurta(e.passagem_em)}
                    </td>
                    <td className="mono px-2 py-1.5 text-tinta-2">
                      {e.evasao_em ? dataCurta(e.evasao_em) : "—"}
                    </td>
                    <td className="mono px-2 py-1.5 text-tinta-2">
                      {dataCurta(e.enviar_ate)}{" "}
                      <span className="text-tinta-3">
                        ({restante(e.enviar_ate, agora)})
                      </span>
                    </td>
                    <td className="mono px-2 py-1.5 text-right text-tinta">
                      {e.valor_centavos == null
                        ? "sem tarifa"
                        : reais(e.valor_centavos)}
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge cor={COR[e.situacao]}>{e.situacao_rotulo}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Painel>
    </section>
  );
}
